import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import SEO from './components/SEO';
import SiteFooter from './components/SiteFooter';

const SITE_URL = 'https://www.greyfielddata.com';

interface Demo {
  title: string;
  platform: string;
  href: string;
  cta: string;
  image: string;
  imageAlt: string;
  problem: string;
  includes: string[];
  note: string;
  post?: { href: string; label: string };
  download?: { href: string; label: string };
  internal?: boolean;
}

const featured: Demo = {
  title: 'Consumer Brand Retention Starter Pack',
  platform: 'Sigma',
  href: 'https://public.sigmacomputing.com/view-workbook/4d4TYxxkl0gwN5ldG0xBP',
  cta: 'Explore in Sigma',
  image: '/images/demos/retention-starter-pack.webp',
  imageAlt: 'Welcome page of the retention starter pack in Sigma, with links to five reports',
  problem: 'Retention numbers that change depending on who you ask, because nobody wrote down which customers each metric includes.',
  includes: [
    'Customer Activity: new, repeat, and reactivated orders by month',
    '90-Day Retention: repeat rate by first-order month and first product',
    '24-Month Frequency: one order or a habit, by acquisition channel',
    'Product Mix: what customers buy second, and how soon',
    'Reactivation: who comes back after a break, and through which channel',
  ],
  note: 'Synthetic data for Willow, a made-up menswear brand.',
  post: { href: '/blog/consumer-brand-retention-dashboard-in-sigma', label: 'Read how the five reports work' },
  download: { href: '/downloads/customer-retention-reporting-white-paper.pdf', label: 'Download the white paper (PDF)' },
};

const demos: Demo[] = [
  {
    title: 'Unified Customer Timeline',
    platform: 'Web app',
    href: '/demos/customer-timeline',
    internal: true,
    cta: 'Open the demo',
    image: '/images/demos/customer-timeline.webp',
    imageAlt: 'One customer timeline joining ad clicks, Shopify orders, Klaviyo emails, Recharge subscription changes, and support tickets',
    problem: 'Acquisition, email, subscription, and support data in separate tools, so nobody can see one customer end to end.',
    includes: [
      'One timeline per customer: ads, site visits, orders, emails, texts, subscription changes, and tickets',
      'Filters by source, and a panel showing how each system\u2019s ID joins',
      'Synthetic data for Brightleaf, a made-up supplement subscription brand',
    ],
    note: 'Runs in your browser on made-up data.',
    post: { href: '/blog/how-to-track-customers-across-platforms', label: 'Read how to build it' },
  },
  {
    title: 'Revenue Goal Calculator',
    platform: 'Google Sheets',
    href: 'https://docs.google.com/spreadsheets/d/1QH-oOpH6Ydoe_0cjnxlKlNQ_Zvqs8LQf8TvYSQWRkTo/edit?usp=drivesdk',
    cta: 'Open the sheet',
    image: '/images/demos/revenue-goal-calculator.webp',
    imageAlt: 'Revenue Goal Calculator in Google Sheets showing launch inputs, funnel assumptions, and a goal snapshot',
    problem: 'A revenue goal for a webinar launch with no plan for the traffic, tickets, and clients it takes.',
    includes: [
      'Works backward from the goal to visitors, tickets, applicants, and clients',
      'Counts tickets, deposits, and program balances without double counting',
      'Rounds to whole-number targets per launch and per webinar',
    ],
    note: 'View-only. Make a copy to use your own numbers.',
    post: { href: '/blog/revenue-goal-calculator-for-webinar-launches', label: 'Read the walkthrough' },
  },
  {
    title: 'Barcode Scanner Portal',
    platform: 'Web app',
    href: 'https://scanner.greyfielddata.com',
    cta: 'Open the scanner',
    image: '/images/demos/barcode-scanner.webp',
    imageAlt: 'Barcode Scanner Portal with scanned items, weights, and totals for the current session',
    problem: 'Weighed products logged by hand, then retyped into a store or inventory system.',
    includes: [
      'Reads GS1-128 labels from a handheld scanner: product code, weight, pack date, and serial',
      'Keeps a product list, session totals, and a history of past sessions',
      'Ends with a submit step, simulated in the demo, where a store or inventory system would receive the items',
    ],
    note: 'Sample business: Greyfield Steaks is made up. Sample data loads on your first visit and stays in your browser.',
  },
];

const tools = [
  {
    href: '/tools/ecommerce-data-stack-quiz',
    label: 'Build your ecommerce data stack',
    body: 'Ten questions about channels, team, and reporting today. Get one pick per layer, from ingestion to BI, the reasons for each, and the phase to start with.',
  },
  {
    href: '/tools/ecommerce-analytics-tool-quiz',
    label: 'Ecommerce analytics setup quiz',
    body: 'Eight questions about channels, revenue, team, and budget. Get a recommended path, from Shopify reports to a packaged tool or a warehouse build, with the reasons.',
  },
  {
    href: '/tools/bi-tool-quiz',
    label: 'BI tool quiz',
    body: 'Eight questions about your warehouse, report builders, and budget. Get a recommendation among Sigma, Power BI, Tableau, Looker, Metabase, Hex, and Omni.',
  },
  {
    href: '/tools/tableau-to-sigma-migration-assessment',
    label: 'Tableau to Sigma migration assessment',
    body: 'Describe your Tableau estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.',
  },
  {
    href: '/tools/power-bi-to-sigma-migration-assessment',
    label: 'Power BI to Sigma migration assessment',
    body: 'Describe your Power BI estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.',
  },
  {
    href: '/tools/tableau-to-sigma-formula-translator',
    label: 'Tableau to Sigma formula translator',
    body: 'Paste a calculated field and get the Sigma formula, with a plan for LOD expressions and table calculations.',
  },
  {
    href: '/tools/dax-to-sigma-formula-translator',
    label: 'DAX to Sigma formula translator',
    body: 'Paste a Power BI measure and get the Sigma formula, with a plan for CALCULATE and time intelligence.',
  },
  {
    href: '/tools/retention-value-calculator',
    label: 'Retention value calculator',
    body: 'See what a better 90-day repeat rate is worth in revenue and gross profit, and what each point is worth.',
  },
  {
    href: '/tools/reporting-time-calculator',
    label: 'Reporting time calculator',
    body: 'Estimate what manual reporting costs and how long automating it would take to pay back.',
  },
  {
    href: '/tools/sigma-migration-readiness',
    label: 'Sigma migration readiness check',
    body: 'Answer 16 questions to see which gaps to close before you scope a move to Sigma.',
  },
];

function ExternalArrow() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 17L17 7M17 7H8M17 7v9" />
    </svg>
  );
}

function DemoBody({ demo }: { demo: Demo }) {
  return (
    <>
      <p className="text-sm font-semibold uppercase tracking-wide text-[#1a4e94]">{demo.platform}</p>
      <h3 className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">{demo.title}</h3>
      <p className="mt-4 leading-7 text-gray-600">
        <span className="font-semibold text-gray-900">The problem: </span>
        {demo.problem}
      </p>
      <ul className="mt-5 space-y-2.5 text-gray-600">
        {demo.includes.map((item) => (
          <li key={item} className="flex gap-3 leading-6">
            <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#1a4e94]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-sm leading-6 text-gray-500">{demo.note}</p>
      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
        {demo.internal ? (
          <Link
            to={demo.href}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0a1628] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#10203a]"
          >
            {demo.cta}
            <span aria-hidden="true">→</span>
          </Link>
        ) : (
        <a
          href={demo.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0a1628] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#10203a]"
        >
          {demo.cta}
          <ExternalArrow />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
        )}
        {demo.post && (
          <Link to={demo.post.href} className="text-sm font-semibold text-[#1a4e94] underline underline-offset-4">
            {demo.post.label}
          </Link>
        )}
        {demo.download && (
          <a href={demo.download.href} download className="text-sm font-semibold text-[#1a4e94] underline underline-offset-4">
            {demo.download.label}
          </a>
        )}
      </div>
    </>
  );
}

export default function DemosPage() {
  return (
    <>
      <SEO
        title="Demos: Sigma retention dashboard, revenue calculator, barcode scanner"
        description="Explore working demos: a five-report customer retention workbook in Sigma, a Google Sheets revenue goal calculator for webinar launches, and a barcode scanning web app."
        type="website"
        url={`${SITE_URL}/demos`}
        keywords={['Sigma retention dashboard demo', 'customer retention dashboard', 'revenue goal calculator', 'barcode scanner web app', 'Greyfield Data demos']}
      />

      <div className="min-h-screen overflow-x-hidden bg-white font-['Inter']">
        <Navigation />

        <section className="relative overflow-hidden bg-[#0a1628] pb-16 pt-32 md:pb-24 md:pt-44">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_10%,rgba(37,99,235,0.22),transparent_45%),radial-gradient(circle_at_10%_100%,rgba(6,182,212,0.12),transparent_40%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
          <div className="relative mx-auto max-w-7xl px-6">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.22em] text-blue-300">Demos</p>
            <h1 className="max-w-4xl text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl">
              Working demos you can open and use
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-gray-400 md:text-xl">
              A retention dashboard in Sigma, a revenue planning calculator in Google Sheets, and a barcode scanning app. Each one runs on sample data, so you can click through without signing up.
            </p>
          </div>
        </section>

        <section aria-labelledby="featured-heading" className="bg-white py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <h2 id="featured-heading" className="sr-only">Demos</h2>
            <article className="grid overflow-hidden rounded-3xl border border-gray-200 shadow-sm lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
              <a href={featured.href} target="_blank" rel="noopener noreferrer" className="group flex items-center border-b border-gray-200 bg-[#eef2f7] lg:border-b-0 lg:border-r" tabIndex={-1} aria-hidden="true">
                <img
                  src={featured.image}
                  alt=""
                  width={1440}
                  height={900}
                  loading="eager"
                  className="block h-auto w-full transition duration-300 group-hover:opacity-95"
                />
              </a>
              <div className="p-7 sm:p-10">
                <DemoBody demo={featured} />
              </div>
            </article>

            <div className="mt-8 grid gap-8 md:grid-cols-2">
              {demos.map((demo) => (
                <article key={demo.title} className="flex flex-col overflow-hidden rounded-3xl border border-gray-200 shadow-sm">
                  <a href={demo.href} {...(demo.internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })} className="block aspect-[16/10] overflow-hidden border-b border-gray-200 bg-gray-50" tabIndex={-1} aria-hidden="true">
                    <img
                      src={demo.image}
                      alt=""
                      width={1440}
                      height={900}
                      loading="lazy"
                      className="h-full w-full object-cover object-left-top transition duration-300 hover:opacity-95"
                    />
                  </a>
                  <div className="flex-1 p-7 sm:p-8">
                    <DemoBody demo={demo} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="tools-heading" className="border-t border-gray-100 bg-gray-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-6">
            <h2 id="tools-heading" className="text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">Free tools on this site</h2>
            <p className="mt-3 max-w-2xl text-gray-600">They run in your browser. Nothing you enter is sent anywhere.</p>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {tools.map((tool) => (
                <Link key={tool.href} to={tool.href} className="group rounded-3xl border border-gray-200 bg-white p-7 transition hover:border-blue-200 hover:shadow-lg sm:p-8">
                  <span className="block text-2xl font-semibold text-gray-900 group-hover:text-[#1a4e94]">{tool.label}</span>
                  <span className="mt-3 block leading-7 text-gray-600">{tool.body}</span>
                  <span className="mt-6 inline-block font-semibold text-[#1a4e94]">Open the tool <span aria-hidden="true">→</span></span>
                </Link>
              ))}
            </div>
            <p className="mt-8 text-gray-600">
              More guides and downloads are on the <Link to="/resources" className="font-semibold text-[#1a4e94] underline underline-offset-4">resources page</Link>.
            </p>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="rounded-3xl bg-[#0a1628] p-8 text-white sm:p-12">
              <h2 className="max-w-3xl text-3xl font-semibold leading-tight tracking-tight md:text-4xl">Want a dashboard, calculator, or internal app like these?</h2>
              <p className="mt-4 max-w-2xl text-lg leading-relaxed text-gray-300">
                Bring the report or workflow you have now. We can talk through what it would take to build it on your data.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="https://cal.com/linustse/meeting-with-linus?overlayCalendar=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-xl bg-white px-7 py-3.5 font-semibold text-[#0a1628] transition hover:bg-gray-100"
                >
                  Discuss a custom build
                </a>
                <Link
                  to="/services/sigma-consulting"
                  className="inline-flex items-center justify-center rounded-xl border border-white/20 px-7 py-3.5 font-semibold text-white transition hover:bg-white/10"
                >
                  Sigma consulting
                </Link>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </>
  );
}
